import type { ListViewItemProps } from "@tql/ui-components-react/kendo/listview";
import { useCallback, useEffect, useRef, useState } from "react";

import type { DefaultDocument } from "~/shared/types/payment-types.ts";

import { createFlyoutListItemRenderer } from "../components/flyout-list-item.tsx";
import type { DocumentViewerState } from "./use-document-viewer";

export type UseDocumentViewerSelectionOptions = {
	viewerState: DocumentViewerState;
	documents: DefaultDocument[] | null;
	selectDocument: (document: DefaultDocument) => void;
	onPrint?: (selectedDocuments: string[]) => void;
	onDownload?: (selectedDocuments: string[]) => void;
};

/**
 * Shared checkbox-selection, item-rendering, and print/download handler logic used by both the
 * desktop and mobile document viewer flyouts, so the two layouts stay behaviorally identical.
 */
// Stable empty-object reference so the "clear selection" branches below can bail out of a state
// update (via the functional setChecked form) once `checked` is already empty, instead of always
// committing a brand-new `{}` reference. `documents` here is frequently a fresh array identity on
// every render of the caller (e.g. query hooks that fall back to `data ?? []`), so without this
// guard this effect would re-fire and re-render on every single render of the parent — an
// infinite loop — any time `DocumentViewer` is mounted but closed/uninitialized.
const EMPTY_CHECKED: Record<string, boolean> = {};

export const useDocumentViewerSelection = ({
	viewerState,
	documents,
	selectDocument,
	onPrint,
	onDownload,
}: UseDocumentViewerSelectionOptions) => {
	const [checked, setChecked] =
		useState<Record<string, boolean>>(EMPTY_CHECKED);
	const isInitializedRef = useRef(false);

	// Auto-select and check the first document on open, handling both cached and async-loaded docs.
	// isInitializedRef prevents re-triggering after the user manually changes their selection.
	useEffect(() => {
		if (!viewerState.isOpen) {
			isInitializedRef.current = false;
			setChecked((prev) => (prev === EMPTY_CHECKED ? prev : EMPTY_CHECKED));
			return;
		}
		if (
			!isInitializedRef.current &&
			documents != null &&
			documents.length > 0
		) {
			isInitializedRef.current = true;
			const firstDoc = documents[0];
			if (firstDoc) {
				setChecked({ [firstDoc.documentFileName]: true });
				selectDocument(firstDoc);
			}
		} else if (!isInitializedRef.current) {
			setChecked((prev) => (prev === EMPTY_CHECKED ? prev : EMPTY_CHECKED));
		}
	}, [viewerState.isOpen, documents, selectDocument]);

	const handleCheckboxChange = useCallback((item: DefaultDocument) => {
		setChecked((prev) => ({
			...prev,
			[item.documentFileName]: !prev[item.documentFileName],
		}));
	}, []);

	const itemRenderer = useCallback(
		(props: ListViewItemProps) =>
			createFlyoutListItemRenderer(
				viewerState.selectedDocument as DefaultDocument,
				checked,
				selectDocument,
				handleCheckboxChange,
			)(props),
		[
			checked,
			selectDocument,
			viewerState.selectedDocument,
			handleCheckboxChange,
		],
	);

	const handlePrintClick = useCallback(() => {
		const selected = Object.entries(checked)
			.filter(([, isChecked]) => isChecked)
			.map(([item]) => item);
		if (onPrint) {
			onPrint(selected);
		}
	}, [checked, onPrint]);

	const handleDownloadClick = useCallback(() => {
		const selected = Object.entries(checked)
			.filter(([, isChecked]) => isChecked)
			.map(([item]) => item);
		if (onDownload) {
			onDownload(selected);
		}
	}, [checked, onDownload]);

	const isNoneSelected = !Object.values(checked).some(Boolean);

	return {
		checked,
		itemRenderer,
		handleCheckboxChange,
		handlePrintClick,
		handleDownloadClick,
		isNoneSelected,
	};
};

/**
 * Subset of {@link useDocumentViewerSelection}'s return value that the presentational desktop and
 * mobile flyouts need. The hook itself must be called exactly once, above whichever component
 * decides which flyout layout to render — otherwise swapping component types (e.g. a responsive
 * breakpoint change) unmounts the hook instance and silently resets the user's selection.
 */
export type DocumentViewerSelectionHandlers = Pick<
	ReturnType<typeof useDocumentViewerSelection>,
	"itemRenderer" | "handlePrintClick" | "handleDownloadClick" | "isNoneSelected"
>;
